import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react';
import SearchStore from 'js/store/SearchStore';
import SeoHelmet from 'js/containers/SEO/SeoHelmet';
import Loader from 'js/components/Common/Loader/Loader';
import Container from 'js/components/Common/Container/Container';
import TopSection from 'js/components/Common/TopSection/TopSection';
import { CommonContext } from 'js/context/CommonContext/CommonContext';

import classes from './Search.module.pcss';
import SearchService from './SearchService';
import SearchResults from './SearchResults/SearchResults';
import SearchSidebar from './SearchSidebar/SearchSidebar';
import parse from 'html-react-parser';
import Heading from 'js/components/Common/Heading/Heading';

const Search = () => {
    const {apiPath} = useContext(CommonContext);
    const nodeRef = useRef(null);
    const {
        searchStatus,
        searchQuery,
        filterTaxData,
        filterEntitiesData,
        orderBy,
        searchPage,
        isAISearch
    } = SearchStore;
    const [searchData, setSearchData] = useState([]);
    const scrollMidRef = useRef(null);
    const scrollEndRef = useRef(null);
    const [load, setLoad] = useState<boolean>(false);
    const [pagesCount, setPagesCount] = useState<number>(0);
    const [postsCount, setPostsCount] = useState<number>(0);

    const noResultMessageHeading = `Sorry, there were no results for <strong>‘${searchQuery}’</strong> <br>`;
    const noResultMessageSubHeading = 'Check your spelling or try a more general term. <br/>';
    const reminderMessage = 'Reminder: Standard search works in English only. <br/>Want better results? Try our new <span>AI Search</span> via the AI toggle.';
    const fetchSearchResults = useCallback(() => {
        (async () => {
            if (!searchQuery?.length) {
                return;
            }
            const response =
                await SearchService.runSearch(
                    apiPath,
                    searchQuery,
                    filterTaxData,
                    filterEntitiesData,
                    orderBy,
                    searchPage,
                    isAISearch
                );
            const { data } = await response.json();
            setSearchData(data?.search?.searchResult);
            setPagesCount(data?.search?.pagesCount);
            setPostsCount(data?.search?.postsCount);
            SearchStore.setEntitiesCounts(data?.search?.entitiesCount);

            SearchStore.setTaxCounts({
                suitableFor: data?.search?.suitableForCount,
                skills: data?.search?.skillsCount,
                productLegacy: data?.search?.productCount,
                subjectMatter: data?.search?.subjectMatterCount,
                language: data?.search?.languageCount,
                articleType: data?.search?.articleTypeCount,
                courseType: data?.search?.courseTypeCount,
                videoType: data?.search?.videoTypeCount,
                manualsType: data?.search?.manualTypeCount
            });

            SearchStore.setSearchStatus(true);
            setLoad(true);
            scrollMidRef.current = null;
            scrollEndRef.current = null;
            SearchStore.setIsSearchTransferData(false);
            window.scrollTo(0, 0);
        })();
    }, [
        isAISearch,
        searchQuery,
        apiPath,
        filterTaxData,
        filterEntitiesData,
        orderBy,
        searchPage
    ]);

    useEffect(() => {
        let isCanceled = false;
        if (!isCanceled) {
            fetchSearchResults();
        }
        return () => {
            isCanceled = true;
        };
    }, [fetchSearchResults]);

    useEffect(() => {
        SearchService.setPageViewEvent();
    }, []);

    useEffect(() => {
        SearchService.setSearchResultEvent(load, postsCount, isAISearch);
    }, [load, postsCount, isAISearch]);

    return (
        <div id={'Search'} className={classes.Search} ref={nodeRef}>
            <SeoHelmet title={'Search'} description={'Search'} canonical={'/search'}/>
            <TopSection pageId={null} pageType={'search'} />
            <Container additionalClass={classes.SearchResultsWrap}>
                {searchQuery.length > 0 && searchData?.length > 0 ?
                    <>
                        <SearchSidebar nodeRef={nodeRef} isAISearch={isAISearch}/>
                        <SearchResults
                            data={searchData}
                            count={postsCount}
                            pages={pagesCount}
                        />
                    </>
                    : searchStatus ?
                        <div className={classes.SearchResultsEmpty}>
                            <Heading text={noResultMessageHeading} />
                            <strong>{parse(`${noResultMessageSubHeading} ${reminderMessage}`)}</strong>
                        </div> :
                        <Loader/>
                }
            </Container>
        </div>
    );
};

export default observer(Search);
